import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Expand, Image, Pause, Play, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useReducedMotion } from 'framer-motion';
import type { DemoScene } from '../projectDemos';
import type { OriginalScreen, ViewerAssets } from '../projectViewerAssets';

type Props = { name: string; scenes: DemoScene[] } & ViewerAssets;
type View = 'interactive' | 'originals' | 'video';

export default function ProjectDemoView({ name, scenes, interactiveDemo, originals, video }: Props) {
  const [index, setIndex] = useState(0);
  const reduced = useReducedMotion();
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<View | null>(null);
  const [originalIndex, setOriginalIndex] = useState(0);
  const [scale, setScale] = useState(0);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const focusBeforeOpen = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const screens: OriginalScreen[] = originals?.length ? originals : scenes.map(scene => ({ src: scene.src, title: scene.title }));
  const original = screens[originalIndex % screens.length];
  const defaultView: View = video ? 'video' : interactiveDemo ? 'interactive' : 'originals';

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { rootMargin: '250px' });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!view) return;
    const bodyOverflow = document.body.style.overflow;
    const root = document.getElementById('root');
    const rootInert = root?.inert ?? false;
    document.body.style.overflow = 'hidden';
    if (root) root.inert = true;
    dialogRef.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setView(null); }
      if (view === 'originals' && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault(); setOriginalIndex(i => (i + (event.key === 'ArrowLeft' ? -1 : 1) + screens.length) % screens.length); setScale(0);
      }
      if (event.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], iframe, video[controls]');
        if (!focusable?.length) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) { event.preventDefault(); first.focus(); }
      }
    };
    const message = (event: MessageEvent) => {
      if (event.source === iframeRef.current?.contentWindow && event.data?.type === 'portfolio-demo-close') setView(null);
    };
    document.addEventListener('keydown', keydown);
    window.addEventListener('message', message);
    return () => {
      document.body.style.overflow = bodyOverflow;
      if (root) root.inert = rootInert;
      document.removeEventListener('keydown', keydown);
      window.removeEventListener('message', message);
      focusBeforeOpen.current?.focus();
    };
  }, [view, screens.length]);

  const open = (next: View) => {
    focusBeforeOpen.current = document.activeElement as HTMLElement;
    setScale(0); setOriginalIndex(0); setFrameLoaded(false); setView(next);
  };
  const scene = scenes[index];
  const animated = !paused && !reduced && visible && !view;
  const src = animated ? scene.src : scene.src.replace(/\.gif$/, '.jpg');
  const label = video ? '观看完整成品' : interactiveDemo ? '打开交互演示' : '放大查看演示';

  return <>
    <figure className="project-demo" ref={ref} aria-label={`${name} 动态演示`}>
      <button type="button" className={`project-demo-screen project-demo-open${video ? ' project-demo-film-cover' : ''}`} aria-label={`${name}：${label}`} onClick={() => open(defaultView)}>
        {video ? <img src={video.poster} alt={video.title} loading="lazy" decoding="async" /> : <img src={src} alt={scene.title} loading="lazy" decoding="async" />}
        <span className="project-demo-badge"><i /> {video ? '完整成品 · 85秒' : animated ? 'GIF · 动态演示' : '项目演示'}</span>
        <span className="project-demo-open-hint">{video ? <Play /> : <Expand />} {label}</span>
      </button>
      <figcaption>
        <span><b>{String(index + 1).padStart(2, '0')}</b> {video ? video.title : scene.title}</span>
        <div className="project-demo-controls">
          {!video && scenes.length > 1 && <>
            <button type="button" aria-label={`${name} 上一段演示`} onClick={() => setIndex((index - 1 + scenes.length) % scenes.length)}><ChevronLeft /></button>
            <small>{index + 1} / {scenes.length}</small>
            <button type="button" aria-label={`${name} 下一段演示`} onClick={() => setIndex((index + 1) % scenes.length)}><ChevronRight /></button>
          </>}
          {originals?.length && !video ? <button type="button" aria-label={`${name} 查看界面原图`} title="查看界面原图" onClick={() => open('originals')}><Image /></button> : null}
          {!reduced && !video && <button type="button" aria-label={`${name} ${paused ? '播放' : '暂停'}预览`} onClick={() => setPaused(!paused)}>{paused ? <Play /> : <Pause />}</button>}
        </div>
      </figcaption>
    </figure>

    {view && createPortal(<div className="project-viewer-backdrop" onClick={event => { if (event.target === event.currentTarget) setView(null); }}>
      <div className="project-viewer" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header className="project-viewer-header"><div><span>PROJECT SHOWCASE</span><h2 id={titleId}>{name}</h2></div><button type="button" className="project-viewer-close" aria-label="关闭展示窗口" onClick={() => setView(null)}><X /></button></header>
        <nav className="project-viewer-tabs" aria-label="展示方式">
          {interactiveDemo && <button type="button" aria-pressed={view === 'interactive'} onClick={() => { setView('interactive'); setFrameLoaded(false); }}>交互导览</button>}
          {!video && <button type="button" aria-pressed={view === 'originals'} onClick={() => setView('originals')}>{originals?.length ? '界面原图' : '原尺寸动图'}</button>}
          {video && <button type="button" aria-pressed={view === 'video'} onClick={() => setView('video')}>完整成品</button>}
        </nav>
        {view === 'interactive' && interactiveDemo && <div className="project-viewer-frame-wrap">
          {!frameLoaded && <span className="project-viewer-loading">正在打开界面导览…</span>}
          <iframe ref={iframeRef} src={interactiveDemo} title={`${name} 独立界面导览`} sandbox="allow-scripts" referrerPolicy="no-referrer" onLoad={() => setFrameLoaded(true)} />
        </div>}
        {view === 'video' && video && <div className="project-viewer-video"><video key={video.src} src={video.src} poster={video.poster} controls autoPlay playsInline preload="metadata" /><p>{video.title}</p></div>}
        {view === 'originals' && <>
          <div className="project-viewer-image-toolbar">
            <button type="button" aria-label="上一张界面原图" disabled={screens.length < 2} onClick={() => { setOriginalIndex((originalIndex - 1 + screens.length) % screens.length); setScale(0); }}><ChevronLeft /></button>
            <span>{original.title} · {originalIndex + 1}/{screens.length}</span>
            <button type="button" aria-label="下一张界面原图" disabled={screens.length < 2} onClick={() => { setOriginalIndex((originalIndex + 1) % screens.length); setScale(0); }}><ChevronRight /></button>
            <div className="project-viewer-zoom"><button type="button" aria-pressed={scale === 0} onClick={() => setScale(0)}>适应窗口</button><button type="button" aria-pressed={scale === 1} onClick={() => setScale(1)}>100% 原图</button><button type="button" aria-label="缩小原图" onClick={() => setScale(Math.max(.5, (scale || 1) - .25))}><ZoomOut /></button><button type="button" aria-label="放大原图" onClick={() => setScale(Math.min(3, (scale || 1) + .25))}><ZoomIn /></button></div>
          </div>
          <div className={`project-viewer-image-area${scale ? ' actual-size' : ''}`}><img src={original.src} alt={original.title} style={scale ? { width: original.width ? original.width * scale : `${scale * 100}%`, maxWidth: 'none', maxHeight: 'none' } : undefined} /></div>
        </>}
      </div>
    </div>, document.body)}
  </>;
}
